import { useCallback } from "react";
import { useRef, useState } from "react";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import parse from "html-react-parser";
import { commonHtmlParserOptions } from "@utils/html-parser";
import { default as NextImage } from "next/image";
import AngleUpIcon from "@svg/angle-up";
import classNames from "classnames";
import Links from "./links";
import { COPY_RIGHT_OWNER } from "@utils/constants";
import { separateMoneyNumbers, toFarsiNumber } from "@utils/digits";
import { useImageFallback } from "@shared/hooks/useImageFallback";
import { BRAND_ASSETS } from "@utils/logoAssets";
import { useLayoutContext } from "@context/layout";
import { useTranslations } from "next-intl";
import HomeAppsFooter from "./footer-home-apps";
import { twMerge } from "tailwind-merge";
import homeApi from "@features/home/helpers/homeApi";

const mainSamandehiImage =
  "https://logo.samandehi.ir/logo.aspx?id=34927&p=qftishwlbsiyodrfyndt";
const secondarySamandehiImage =
  "https://faradars.org/wp-content/uploads/2024/05/20/samandehi-svgrepo-com.svg";
const mainEnamadImage =
  "https://trustseal.enamad.ir/logo.aspx?id=82480&Code=Rsc5FN8cQujc0rvzJAs6X8K4xRNjhRCf";
const secondaryEnamdImage =
  "https://faradars.org/wp-content/uploads/2024/05/20/664b90e55e5de-enamad-svgrepo-com.svg";

const ECUNION = {
  main: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQwIiBoZWlnaHQ9IjM2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KCTxwYXRoIGQ9Im0xMjAgMjQzbDk0LTU0IDAtMTA5IC05NCA1NCAwIDEwOSAwIDB6IiBmaWxsPSIjODA4Mjg1Ii8+Cgk8cGF0aCBkPSJtMTIwIDI1NGwtMTAzLTYwIDAtMTE5IDEwMy02MCAxMDMgNjAgMCAxMTkgLTEwMyA2MHoiIHN0eWxlPSJmaWxsOm5vbmU7c3Ryb2tlLWxpbmVqb2luOnJvdW5kO3N0cm9rZS13aWR0aDo1O3N0cm9rZTojMDBhZWVmIi8+Cgk8cGF0aCBkPSJtMjE0IDgwbC05NC01NCAtOTQgNTQgOTQgNTQgOTQtNTR6IiBmaWxsPSIjMDBhZWVmIi8+Cgk8cGF0aCBkPSJtMjYgODBsMCAxMDkgOTQgNTQgMC0xMDkgLTk0LTU0IDAgMHoiIGZpbGw9IiM1ODU5NWIiLz4KCTxwYXRoIGQ9Im0xMjAgMTU3bDQ3LTI3IDAtMjMgLTQ3LTI3IC00NyAyNyAwIDU0IDQ3IDI3IDQ3LTI3IiBzdHlsZT0iZmlsbDpub25lO3N0cm9rZS1saW5lY2FwOnJvdW5kO3N0cm9rZS1saW5lam9pbjpyb3VuZDtzdHJva2Utd2lkdGg6MTU7c3Ryb2tlOiNmZmYiLz4KCTx0ZXh0IHg9IjE1IiB5PSIzMDAiIGZvbnQtc2l6ZT0iMjVweCIgZm9udC1mYW1pbHk9IidCIFlla2FuJyIgc3R5bGU9ImZpbGw6IzI5Mjk1Mjtmb250LXdlaWdodDpib2xkIj7Yudi22Ygg2KfYqtit2KfYr9uM2Ycg2qnYtNmI2LHbjDwvdGV4dD4KCTx0ZXh0IHg9IjgiIHk9IjM0MyIgZm9udC1zaXplPSIyNXB4IiBmb250LWZhbWlseT0iJ0IgWWVrYW4nIiBzdHlsZT0iZmlsbDojMjkyOTUyO2ZvbnQtd2VpZ2h0OmJvbGQiPtqp2LPYqCDZiCDaqdin2LHZh9in24wg2YXYrNin2LLbjDwvdGV4dD4KPC9zdmc+",
  secondary:
    "https://faradars.org/wp-content/uploads/2025/12/21/لوگو-اتحادیه-فضای-مجازی.png",
};

interface Props {
  showHomeAppFooter: boolean;
  coloredMainFooterBg: boolean;
}

const MainFooter: React.FC<Props> = ({
  showHomeAppFooter,
  coloredMainFooterBg,
}) => {
  const t = useTranslations("footer");
  const { staticData } = useLayoutContext();

  const enamadSrcImage = useImageFallback(
    mainEnamadImage,
    secondaryEnamdImage,
    mainEnamadImage
  );

  const imageSource = useImageFallback(
    mainSamandehiImage,
    secondarySamandehiImage,
    mainSamandehiImage
  );

  const ecunionSource = useImageFallback(
    ECUNION.main,
    ECUNION.secondary,
    ECUNION.main
  );

  const contentElement = useRef<any>(null);

  const [showMore, setShowMore] = useState(false);
  const [height, setHeight] = useState<string>("0px");

  const { data } = useRequestImmutable(homeApi.getFooter());
  const replaceValues: { [key: string]: string } = {
    "{{sumOfProductTimes}}": String(
      staticData?.statistics?.sumOfProductTimes || ""
    ),
    "{{countOfProducts}}": String(
      staticData?.statistics?.countOfProducts || ""
    ),
    "{{instructorsCount}}": String(
      staticData?.statistics?.instructorsCount || ""
    ),
  };

  const replaceFooterDescriptionValues = useCallback((summary: string) => {
    const regex = new RegExp(Object.keys(replaceValues).join("|"), "gi");

    return summary.replace(
      regex,
      (matched) =>
        separateMoneyNumbers(toFarsiNumber(replaceValues[matched])) || ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const HandleOpening = () => {
    setShowMore(!showMore);
    setHeight(
      !showMore ? `${contentElement?.current?.scrollHeight + 20}px` : "0px"
    );
  };

  return (
    <div className="flex flex-col max-w-[100vw] overflow-x-hidden">
      <div
        className={twMerge(
          "py-8 w-full flex justify-center",
          coloredMainFooterBg ? "bg-fds-canvas-secondary w-screen" : " bg-white"
        )}
      >
        <div className="px-6 md:px-16 lg:px-fds-120 xl:px-16 flex lg:flex-row flex-col justify-between  gap-x-[106px] gap-y-3 w-full max-w-[1920px]">
          <div className="w-full max-w-[846px]">
            <NextImage
              src={
                data?.data.brand.image
                  ? data.data.brand.image
                  : BRAND_ASSETS.footerLogo
              }
              alt={data?.data.brand.description || "footerLogo"}
              width={125}
              height={30}
            />

            <div
              className={classNames(
                data?.data.content.summary === null && "hidden"
              )}
            >
              <div className="font-light [&>p]:leading-[1.7rem] [&>p]:mb-3 [&>p]:text-justify [&>p]:text-sm [&>p]:font-normal [&>p]:text-[#52565C]">
                {data?.data.content.summary
                  ? parse(
                      replaceFooterDescriptionValues(data.data.content.summary),
                      commonHtmlParserOptions()
                    )
                  : null}
                {!showMore && data?.data.content.description !== null && (
                  <span
                    className="font-normal text-sm text-[#1E88E5] h-10 flex items-center"
                    role={"button"}
                    onClick={HandleOpening}
                  >
                    {` ${t("seeMore")}`}
                    <AngleUpIcon className="h-2 w-3 mr-1 transform rotate-180" />
                  </span>
                )}
              </div>
              <div
                ref={contentElement}
                className="m-0 overflow-hidden  p-0 transition-all duration-500 ease-in-out [&>p]:leading-[1.7rem] [&>p]:mb-3 [&>p]:text-justify [&>p]:text-sm [&>p]:font-normal [&>p]:text-zinc-500"
                style={{ height: height }}
              >
                {parse(
                  replaceFooterDescriptionValues(
                    data?.data.content.description || ""
                  ),
                  commonHtmlParserOptions()
                )}
                <span
                  className="font-normal text-sm text-[#1E88E5] flex items-center"
                  role={"button"}
                  onClick={HandleOpening}
                >
                  {` ${t("seeLess")}`}
                  <AngleUpIcon className=" h-2 w-3 mr-1" />
                </span>
              </div>
            </div>
          </div>
          <div className="flex sm:flex-row flex-col w-full lg:max-w-72">
            <div className="flex w-full flex-row justify-start sm:justify-end gap-x-3">
              <div className="cursor-pointer  w-20 h-20  border rounded-lg border-zinc-200 bg-fds-primary flex items-center justify-center">
                <img
                  referrerPolicy="origin"
                  id="rgvjwlaosizpnbqejzpe"
                  onClick={() =>
                    window.open(
                      "https://logo.samandehi.ir/Verify.aspx?id=34927&p=xlaoaodspfvluiwkjyoe",
                      "Popup",
                      "toolbar=no, scrollbars=no, location=no, statusbar=no, menubar=no, resizable=0, width=450, height=630, top=30"
                    )
                  }
                  height={60}
                  width={60}
                  alt="ساماندهی"
                  src={imageSource}
                />
              </div>

              <div className=" w-20 h-20 border rounded-lg border-zinc-200 bg-fds-primary flex items-center justify-center">
                <a
                  href="https://trustseal.enamad.ir/?id=82480&Code=Rsc5FN8cQujc0rvzJAs6X8K4xRNjhRCf"
                  id="enamad-link"
                  rel="noopener"
                  target="_blank"
                >
                  <img
                    referrerPolicy="origin"
                    loading="lazy"
                    decoding="async"
                    src={enamadSrcImage}
                    height={60}
                    width={60}
                    alt="نمادالکترونیکی"
                    style={{ cursor: "pointer" }}
                    data-code="Rsc5FN8cQujc0rvzJAs6X8K4xRNjhRCf"
                  />
                </a>
              </div>
              <div className="cursor-pointer w-20 h-20 border rounded-lg border-zinc-200 bg-fds-primary flex items-center justify-center overflow-hidden">
                <img
                  src={ecunionSource}
                  alt="اتحادیه کسب و کارهای مجازی"
                  style={{
                    cursor: "pointer",
                    width: "60px",
                    height: "auto",
                    maxHeight: "60px",
                    objectFit: "contain",
                  }}
                  onClick={() =>
                    window.open(
                      "https://ecunion.ir/verify/faradars.org?token=56209196937dac81d47c",
                      "Popup",
                      "toolbar=no, location=no, statusbar=no, menubar=no, scrollbars=1, resizable=0, width=580, height=600, top=30"
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showHomeAppFooter && <HomeAppsFooter />}
      <Links links={data?.data.links} />
      <div className="flex justify-center items-center py-2">
        <p className="text-zinc-500 text-[10px] font-normal">
          {COPY_RIGHT_OWNER}
        </p>
      </div>
    </div>
  );
};

export default MainFooter;
